/* Page BRVM — Value Screener pour la Bourse Régionale des Valeurs Mobilières (UEMOA).
 *
 * - Découverte IA hybride (NOAM + Tavily) : génère candidats + 5 ans de financials
 * - Table avec ratios calculés : B/M, PER, E/P, CAGR CA/RAO/RN, marges, ROE, score composite
 * - Graphiques 5Y par compagnie (CA, RAO, RN, Equity)
 * - Filtres : score min, secteur, pays
 */
import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { notify, fmtError } from "@/lib/notify";
import {
  TrendingUp, Sparkles, Loader2, RefreshCw, Plus, X, Filter, AlertTriangle,
  BarChart3, Check, Edit2, Trash2, Globe, ArrowUpRight,
} from "lucide-react";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar, Legend,
} from "recharts";
import ModuleHelpButton from "@/components/ModuleHelpButton";
import TradingIA from "@/pages/saas/TradingIA";

const VERDICT_META = {
  insufficient_data: {
    label: "Données insuffisantes",
    color: "#71717A",
    bg: "rgba(113,113,122,0.12)",
    desc: "Couverture de données trop faible pour établir un score fiable",
  },
  strong_value: { label: "Strong value", color: "#1D9E75", bg: "rgba(29,158,117,0.12)", desc: "Sous-cotation forte sur plusieurs axes" },
  value:        { label: "Value",        color: "#378ADD", bg: "rgba(55,138,221,0.12)", desc: "Valorisation attractive" },
  fair:         { label: "Fair",         color: "#EF9F27", bg: "rgba(239,159,39,0.12)", desc: "Cotation proche du juste" },
  expensive:    { label: "Cher",         color: "#C26EE7", bg: "rgba(194,110,231,0.12)", desc: "Surcotation modérée" },
  overvalued:   { label: "Surcoté",      color: "#E24B4A", bg: "rgba(226,75,74,0.12)", desc: "Surcotation forte (PER élevé)" },
  bubble:       { label: "Bulle",        color: "#FF2E63", bg: "rgba(255,46,99,0.18)", desc: "PER >100 — possible bulle" },
};

const COUNTRY_FLAGS = {
  CI: "🇨🇮", SN: "🇸🇳", BF: "🇧🇫", ML: "🇲🇱",
  TG: "🇹🇬", BJ: "🇧🇯", NE: "🇳🇪", GW: "🇬🇼",
};

const CONTINENT_META = {
  afrique:        { label: "Afrique" },
  europe:         { label: "Europe" },
  amerique_nord:  { label: "Amérique du Nord" },
  amerique_sud:   { label: "Amérique du Sud" },
  asie:           { label: "Asie" },
  moyen_orient:   { label: "Moyen-Orient" },
  oceanie:        { label: "Océanie" },
};

const fmt = {
  pct: (v) => (v == null ? "—" : `${(v * 100).toFixed(1)}%`),
  num: (v) => (v == null ? "—" : v.toFixed(2)),
  bigM: (v) => {
    if (v == null) return "—";
    if (Math.abs(v) >= 1e6) return `${(v / 1e6).toFixed(1)} Md`;
    if (Math.abs(v) >= 1e3) return `${(v / 1e3).toFixed(1)} M`;
    return v.toFixed(0);
  },
  xof: (v) => (v == null ? "—" : `${v.toLocaleString("fr-FR")} XOF`),
};

export default function Brvm() {
  const location = useLocation();
  const [mainTab, setMainTab] = useState(() =>
    new URLSearchParams(window.location.search).get("tab") === "trading" ? "trading" : "screener");
  // D3 — deep link/navigation par System : ?tab=trading|screener pilote l'onglet
  useEffect(() => {
    const t = new URLSearchParams(location.search).get("tab");
    if (t === "trading" || t === "screener") setMainTab(t);
  }, [location.search]);
  const focusId = useMemo(() => new URLSearchParams(location.search).get("focus") || "", [location.search]);
  const [screen, setScreen] = useState(null);
  const [countries, setCountries] = useState([]);
  const [exchanges, setExchanges] = useState([]);
  const [sectorCats, setSectorCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    min_score: 0,
    sector: "",
    sector_category: "",
    direction: "",
    // P2 — cascade géographique
    continent: "",
    region: "",
    country_name: "",
    exchange: "",
  });
  const [discoverOpen, setDiscoverOpen] = useState(false);
  const [yfOpen, setYfOpen] = useState(false);
  const [yfBusy, setYfBusy] = useState(false);
  const [discovering, setDiscovering] = useState(false);
  const [detail, setDetail] = useState(null);   // {company, financials, metrics}
  const [editor, setEditor] = useState(null);   // {kind:"company"|"financial", item?, companyId?}
  const [sortKey, setSortKey] = useState("composite_score"); // sortable columns
  const [sortDir, setSortDir] = useState("desc");

  const load = async () => {
    setLoading(true);
    try {
      const [s, c] = await Promise.all([
        api.get("/workspace/brvm/screen", { params: {
          min_score: filters.min_score || undefined,
          sector: filters.sector || undefined,
          sector_category: filters.sector_category || undefined,
          continent: filters.continent || undefined,
          region: filters.region || undefined,
          country_name: filters.country_name || undefined,
          exchange: filters.exchange || undefined,
          direction: filters.direction || undefined,
        } }),
        api.get("/workspace/brvm/countries"),
      ]);
      setScreen(s.data);
      setCountries(c.data?.items || []);
      setExchanges(c.data?.exchanges || []);
      setSectorCats(c.data?.sector_categories || []);
    } catch (e) {
      setError(fmtError(e, "Chargement Marchés financiers impossible."));
    } finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- `load` est stable, seuls les filtres déclenchent
  useEffect(() => { load(); }, [filters.min_score, filters.sector, filters.sector_category, filters.continent, filters.region, filters.country_name, filters.exchange, filters.direction]);

  // Scroll/highlight a company when ?focus=ID is in URL (deep link from Radar)
  useEffect(() => {
    if (!focusId || !screen?.items?.length) return;
    const el = document.querySelector(`[data-row-id="${focusId}"]`);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      el.classList.add("ring-2", "ring-[#1D9E75]");
      const t = setTimeout(() => el.classList.remove("ring-2", "ring-[#1D9E75]"), 4000);
      return () => clearTimeout(t);
    }
  }, [focusId, screen]);

  // Sort rows according to selected column
  const sortedItems = useMemo(() => {
    const items = [...(screen?.items || [])];
    const getVal = (r) => {
      if (sortKey === "ticker") return r.ticker;
      if (sortKey === "name") return r.name;
      if (sortKey === "exchange") return r.exchange || "";
      const m = r.metrics || {};
      return m[sortKey] ?? -Infinity;
    };
    items.sort((a, b) => {
      const av = getVal(a), bv = getVal(b);
      if (typeof av === "string" && typeof bv === "string") {
        return sortDir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
      }
      const na = av == null ? -Infinity : Number(av);
      const nb = bv == null ? -Infinity : Number(bv);
      return sortDir === "asc" ? na - nb : nb - na;
    });
    return items;
  }, [screen, sortKey, sortDir]);

  const toggleSort = (k) => {
    if (sortKey === k) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(k); setSortDir("desc"); }
  };

  // P2 — cascade géographique : Continent → Région → Pays → Bourse
  // Les options de chaque niveau dépendent strictement des choix amont
  const geoCascade = useMemo(() => {
    const dedupSort = (arr) => [...new Set(arr)].sort((a, b) => a.localeCompare(b, "fr"));
    const matchUp = (ex) =>
      (!filters.continent || ex.continent === filters.continent) &&
      (!filters.region || ex.region === filters.region) &&
      (!filters.country_name || ex.country === filters.country_name);
    const continents = dedupSort(exchanges.map((e) => e.continent).filter(Boolean));
    const regions = dedupSort(
      exchanges.filter((e) => !filters.continent || e.continent === filters.continent).map((e) => e.region).filter(Boolean)
    );
    const countriesList = dedupSort(
      exchanges
        .filter((e) => (!filters.continent || e.continent === filters.continent) && (!filters.region || e.region === filters.region))
        .map((e) => e.country)
        .filter(Boolean)
    );
    const exchangesFiltered = exchanges.filter(matchUp).sort((a, b) => a.code.localeCompare(b.code));
    return { continents, regions, countriesList, exchangesFiltered };
  }, [exchanges, filters.continent, filters.region, filters.country_name]);

  const setGeo = (patch) => {
    // Strict cascade : reset des enfants quand un parent change
    const next = { ...filters, ...patch };
    if ("continent" in patch) { next.region = ""; next.country_name = ""; next.exchange = ""; }
    else if ("region" in patch) { next.country_name = ""; next.exchange = ""; }
    else if ("country_name" in patch) { next.exchange = ""; }
    setFilters(next);
  };

  const refreshYf = async (cid, ticker) => {
    setYfBusy(true);
    try {
      const r = await api.post(`/workspace/brvm/companies/${cid}/refresh-yfinance`);
      notify.success(`${ticker} : ${r.data.years_imported} années importées, prix ${r.data.price} ${r.data.currency}.`);
      await load();
      if (detail && detail.company.id === cid) {
        const d = await api.get(`/workspace/brvm/companies/${cid}/detail`);
        setDetail(d.data);
      }
    } catch (e) {
      notify.error(fmtError(e, "Refresh yfinance impossible."));
    } finally { setYfBusy(false); }
  };

  const openDetail = async (cid) => {
    try {
      const r = await api.get(`/workspace/brvm/companies/${cid}/detail`);
      setDetail(r.data);
    } catch (e) { notify.error(fmtError(e)); }
  };

  const removeCompany = async (c) => {
    if (!window.confirm(`Supprimer ${c.ticker} — ${c.name} et tous ses snapshots ?`)) return;
    try {
      await api.delete(`/workspace/brvm/companies/${c.id}`);
      notify.success("Compagnie supprimée.");
      setDetail(null);
      await load();
    } catch (e) { notify.error(fmtError(e)); }
  };

  const sectors = useMemo(() => {
    const s = new Set();
    (screen?.items || []).forEach((x) => x.sector && s.add(x.sector));
    return Array.from(s).sort();
  }, [screen]);

  return (
    <div className="space-y-4" data-testid="brvm-root">
      <div className="inline-flex rounded-md border border-white/10 bg-black/30 p-0.5" data-testid="brvm-main-tabs">
        <button
          type="button"
          onClick={() => setMainTab("screener")}
          className={`text-[11px] px-3 py-1.5 rounded transition-colors ${mainTab === "screener" ? "bg-[#1D9E75]/20 text-[#1D9E75]" : "text-zinc-400 hover:text-white"}`}
          data-testid="brvm-tab-screener"
        >
          Screener actions
        </button>
        <button
          type="button"
          onClick={() => setMainTab("trading")}
          className={`text-[11px] px-3 py-1.5 rounded transition-colors ${mainTab === "trading" ? "bg-[#378ADD]/20 text-[#378ADD]" : "text-zinc-400 hover:text-white"}`}
          data-testid="brvm-tab-trading"
        >
          Trading IA (simulation)
        </button>
      </div>
      {mainTab === "trading" ? <TradingIA /> : (<>
      <header className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2 text-[#1D9E75] noam-overline">
            <TrendingUp className="w-3 h-3" /> TACTIQUE · MARCHÉS FINANCIERS
          </div>
          <h1 className="noam-display text-2xl font-bold tracking-tight mt-1">Actions sous-cotées — Bourses mondiales</h1>
          <p className="text-zinc-400 text-xs mt-1 max-w-3xl">
            Recherche d'actions sous-cotées sur les bourses du monde (BRVM, NYSE, NASDAQ, Euronext, LSE, JSE, Tokyo, Hong Kong, etc.). Filtre par catégorie sectorielle (Tech, Santé, Matières premières…) et 6 critères value : Book-to-Market, PER, croissance RAO/CA 5Y, marges, ROE. Score composite 0-100.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <ModuleHelpButton moduleKey="brvm" />
          <button onClick={load} disabled={loading} className="btn-ghost text-xs disabled:opacity-50" data-testid="brvm-refresh">
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} Actualiser
          </button>
          <button onClick={() => setEditor({ kind: "company" })} className="btn-ghost text-xs" data-testid="brvm-add-company">
            <Plus className="w-3.5 h-3.5" /> Ajouter
          </button>
          <button onClick={() => setYfOpen(true)} className="btn-ghost text-xs" data-testid="brvm-yfinance-btn" title="Import groupé via yfinance (NYSE/NASDAQ/Euronext/LSE/TSX/etc.)">
            <Globe className="w-3.5 h-3.5" /> yfinance
          </button>
          <button onClick={() => setDiscoverOpen(true)} className="btn-primary text-xs" data-testid="brvm-discover-btn">
            <Sparkles className="w-3.5 h-3.5" /> Découvrir par IA
          </button>
        </div>
      </header>

      {/* Stats bar */}
      {screen?.stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2" data-testid="brvm-stats">
          <StatCard label="Compagnies" value={screen.stats.count} />
          <StatCard label="Score moyen" value={screen.stats.avg_score} suffix="/100" />
          <StatCard label="Strong value" value={screen.stats.strong_value} color={VERDICT_META.strong_value.color} />
          <StatCard label="Value" value={screen.stats.value} color={VERDICT_META.value.color} />
          <StatCard label="Cher" value={screen.stats.expensive} color={VERDICT_META.expensive.color} />
        </div>
      )}

      {/* Filters */}
      <div className="noam-card p-3 flex items-center gap-3 flex-wrap" data-testid="brvm-filters">
        <Filter className="w-3.5 h-3.5 text-zinc-500" />
        <div className="flex items-center gap-1.5">
          <label className="text-[10px] uppercase tracking-wider text-zinc-500">Direction</label>
          <select value={filters.direction} onChange={(e) => setFilters({ ...filters, direction: e.target.value })} className="noam-input text-xs py-1" data-testid="brvm-filter-direction">
            <option value="">Tous verdicts</option>
            <option value="undervalued">Sous-cotés (Strong + Value)</option>
            <option value="fair">Fair</option>
            <option value="overvalued">Surcotés / Bulle</option>
          </select>
        </div>
        <div className="flex items-center gap-1.5">
          <label className="text-[10px] uppercase tracking-wider text-zinc-500">Score min</label>
          <input
            type="number" min="0" max="100" value={filters.min_score}
            onChange={(e) => setFilters({ ...filters, min_score: Math.max(0, Math.min(100, Number(e.target.value) || 0)) })}
            className="noam-input w-16 text-xs py-1"
            data-testid="brvm-filter-score"
          />
        </div>
        <div className="flex items-center gap-1.5">
          <label className="text-[10px] uppercase tracking-wider text-zinc-500">Catégorie</label>
          <select value={filters.sector_category} onChange={(e) => setFilters({ ...filters, sector_category: e.target.value })} className="noam-input text-xs py-1" data-testid="brvm-filter-sector-category">
            <option value="">Toutes</option>
            {sectorCats.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div className="flex items-center gap-1.5">
          <label className="text-[10px] uppercase tracking-wider text-zinc-500">Continent</label>
          <select
            value={filters.continent}
            onChange={(e) => setGeo({ continent: e.target.value })}
            className="noam-input text-xs py-1"
            data-testid="brvm-filter-continent"
          >
            <option value="">Tous</option>
            {geoCascade.continents.map((c) => (
              <option key={c} value={c}>{CONTINENT_META[c]?.label || c}</option>
            ))}
          </select>
        </div>
        <div className="flex items-center gap-1.5">
          <label className="text-[10px] uppercase tracking-wider text-zinc-500">Région</label>
          <select
            value={filters.region}
            onChange={(e) => setGeo({ region: e.target.value })}
            className="noam-input text-xs py-1 disabled:opacity-40"
            disabled={!filters.continent && geoCascade.regions.length > 12}
            data-testid="brvm-filter-region"
          >
            <option value="">Toutes</option>
            {geoCascade.regions.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>
        <div className="flex items-center gap-1.5">
          <label className="text-[10px] uppercase tracking-wider text-zinc-500">Pays</label>
          <select
            value={filters.country_name}
            onChange={(e) => setGeo({ country_name: e.target.value })}
            className="noam-input text-xs py-1 disabled:opacity-40"
            disabled={!filters.continent}
            data-testid="brvm-filter-country-name"
          >
            <option value="">Tous</option>
            {geoCascade.countriesList.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div className="flex items-center gap-1.5">
          <label className="text-[10px] uppercase tracking-wider text-zinc-500">Bourse</label>
          <select
            value={filters.exchange}
            onChange={(e) => setFilters({ ...filters, exchange: e.target.value })}
            className="noam-input text-xs py-1 disabled:opacity-40"
            disabled={!filters.country_name && geoCascade.exchangesFiltered.length > 8}
            data-testid="brvm-filter-exchange"
          >
            <option value="">Toutes ({geoCascade.exchangesFiltered.length})</option>
            {geoCascade.exchangesFiltered.map((e) => <option key={e.code} value={e.code}>{e.code} — {e.name}</option>)}
          </select>
        </div>
        <div className="flex items-center gap-1.5">
          <label className="text-[10px] uppercase tracking-wider text-zinc-500">Secteur libre</label>
          <select value={filters.sector} onChange={(e) => setFilters({ ...filters, sector: e.target.value })} className="noam-input text-xs py-1" data-testid="brvm-filter-sector">
            <option value="">Tous</option>
            {sectors.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <button
          type="button"
          onClick={() => setFilters({ ...filters, continent: "", region: "", country_name: "", exchange: "" })}
          disabled={!(filters.continent || filters.region || filters.country_name || filters.exchange)}
          className="text-[10px] text-[#EF9F27] hover:underline disabled:opacity-30 disabled:cursor-not-allowed"
          data-testid="brvm-filter-clear-geo"
        >
          Effacer géo
        </button>
        {screen?.weights && (
          <div className="ml-auto text-[10px] text-zinc-500 italic">
            Poids : B/M {Math.round(screen.weights.book_to_market * 100)}% · E/P {Math.round(screen.weights.earnings_yield * 100)}% · CAGR RAO {Math.round(screen.weights.cagr_rao * 100)}% · CAGR CA {Math.round(screen.weights.cagr_revenue * 100)}% · Marge {Math.round(screen.weights.net_margin_avg * 100)}% · ROE {Math.round(screen.weights.roe_avg * 100)}%
          </div>
        )}
      </div>

      {error && (
        <div className="flex items-start gap-2 p-3 rounded-md border border-[#E24B4A]/30 bg-[#E24B4A]/10 text-[#FCA5A5] text-xs">
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5" /><span>{error}</span>
          <button className="ml-auto" onClick={() => setError(null)}><X className="w-3 h-3" /></button>
        </div>
      )}

      {/* Table */}
      <div className="noam-card overflow-hidden" data-testid="brvm-table">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-white/[0.02] border-b border-white/5">
              <tr className="text-left text-[10px] uppercase tracking-wider text-zinc-500">
                <Th onClick={() => toggleSort("ticker")} active={sortKey === "ticker"} dir={sortDir}>Ticker</Th>
                <Th onClick={() => toggleSort("name")} active={sortKey === "name"} dir={sortDir}>Nom · Secteur</Th>
                <Th onClick={() => toggleSort("market_cap")} active={sortKey === "market_cap"} dir={sortDir} right title="Capitalisation boursière">Capi</Th>
                <Th onClick={() => toggleSort("book_to_market")} active={sortKey === "book_to_market"} dir={sortDir} right title="Book-to-Market ratio">B/M</Th>
                <Th onClick={() => toggleSort("per")} active={sortKey === "per"} dir={sortDir} right title="Price/Earnings Ratio">PER</Th>
                <Th onClick={() => toggleSort("earnings_yield")} active={sortKey === "earnings_yield"} dir={sortDir} right title="Earnings Yield (1/PER)">E/P</Th>
                <Th onClick={() => toggleSort("cagr_revenue")} active={sortKey === "cagr_revenue"} dir={sortDir} right title="CAGR CA 5Y">CAGR CA</Th>
                <Th onClick={() => toggleSort("cagr_rao")} active={sortKey === "cagr_rao"} dir={sortDir} right title="CAGR RAO 5Y">CAGR RAO</Th>
                <Th onClick={() => toggleSort("net_margin_avg")} active={sortKey === "net_margin_avg"} dir={sortDir} right title="Marge nette moyenne 5Y">Marge nette</Th>
                <Th onClick={() => toggleSort("roe_avg")} active={sortKey === "roe_avg"} dir={sortDir} right title="ROE moyenne 5Y">ROE</Th>
                <Th onClick={() => toggleSort("composite_score")} active={sortKey === "composite_score"} dir={sortDir} right>Score</Th>
                <th className="px-3 py-2.5"></th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={12} className="text-center py-12 text-zinc-500"><Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Chargement…</td></tr>
              )}
              {!loading && sortedItems.length === 0 && (
                <tr><td colSpan={12} className="text-center py-12 text-zinc-500" data-testid="brvm-empty">
                  Aucune compagnie. Cliquez <strong className="text-[#EF9F27]">Découvrir par IA</strong> ou <strong className="text-[#378ADD]">yfinance</strong> pour générer automatiquement.
                </td></tr>
              )}
              {!loading && sortedItems.map((r) => {
                const m = r.metrics;
                const v = VERDICT_META[m.verdict] || VERDICT_META.fair;
                return (
                  <tr key={r.id} data-row-id={r.id} className="border-b border-white/5 hover:bg-white/[0.02] cursor-pointer transition-shadow" onClick={() => openDetail(r.id)} data-testid={`brvm-row-${r.ticker}`}>
                    <td className="px-3 py-2.5">
                      <div className="font-mono font-bold text-[#378ADD]">{r.ticker}</div>
                      <div className="text-[9px] text-zinc-500">
                        {r.exchange && r.exchange !== "BRVM" ? r.exchange : (COUNTRY_FLAGS[r.country] || "") + " " + r.country}
                      </div>
                    </td>
                    <td className="px-3 py-2.5 max-w-[220px]">
                      <div className="truncate text-zinc-100" title={r.name}>{r.name}</div>
                      <div className="text-[10px] text-zinc-500">
                        {r.sector_category && <span className="text-[#1D9E75]">{r.sector_category}</span>}
                        {r.sector_category && r.sector && " · "}
                        {r.sector || (!r.sector_category ? "—" : "")}
                      </div>
                    </td>
                    <td className="px-3 py-2.5 text-right text-zinc-300 font-mono">{fmt.bigM(m.market_cap)}</td>
                    <td className="px-3 py-2.5 text-right font-mono">{fmt.num(m.book_to_market)}</td>
                    <td className="px-3 py-2.5 text-right font-mono">{fmt.num(m.per)}</td>
                    <td className="px-3 py-2.5 text-right font-mono">{fmt.pct(m.earnings_yield)}</td>
                    <td className="px-3 py-2.5 text-right font-mono" style={{ color: m.cagr_revenue > 0 ? "#1D9E75" : (m.cagr_revenue < 0 ? "#E24B4A" : undefined) }}>{fmt.pct(m.cagr_revenue)}</td>
                    <td className="px-3 py-2.5 text-right font-mono" style={{ color: m.cagr_rao > 0 ? "#1D9E75" : (m.cagr_rao < 0 ? "#E24B4A" : undefined) }}>{fmt.pct(m.cagr_rao)}</td>
                    <td className="px-3 py-2.5 text-right font-mono">{fmt.pct(m.net_margin_avg)}</td>
                    <td className="px-3 py-2.5 text-right font-mono">{fmt.pct(m.roe_avg)}</td>
                    <td className="px-3 py-2.5 text-right">
                      <div className="inline-flex items-center gap-1.5 px-2 py-1 rounded" style={{ background: v.bg, color: v.color }}>
                        <span className="font-bold">{m.composite_score?.toFixed(0) ?? "—"}</span>
                        <span className="text-[9px] uppercase tracking-wider">{v.label}</span>
                      </div>
                    </td>
                    <td className="px-3 py-2.5 text-right">
                      <div className="inline-flex items-center gap-1">
                        {r.exchange && r.exchange !== "BRVM" && (
                          <button onClick={(e) => { e.stopPropagation(); refreshYf(r.id, r.ticker); }} disabled={yfBusy} className="text-zinc-500 hover:text-[#1D9E75] p-1 disabled:opacity-50" title="Rafraîchir via yfinance" data-testid={`brvm-row-yf-${r.ticker}`}>
                            <Globe className="w-3.5 h-3.5" />
                          </button>
                        )}
                        <button onClick={(e) => { e.stopPropagation(); openDetail(r.id); }} className="text-zinc-500 hover:text-white p-1" title="Voir détail">
                          <BarChart3 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {discoverOpen && (
        <DiscoverModal
          countries={countries}
          exchanges={exchanges}
          sectorCats={sectorCats}
          existingSectors={sectors}
          onClose={() => setDiscoverOpen(false)}
          onDone={async (res) => {
            setDiscoverOpen(false);
            notify.success(`${res.created} créée(s), ${res.updated} mise(s) à jour, ${res.financials_imported} snapshots financiers importés.`);
            await load();
          }}
          busy={discovering}
          setBusy={setDiscovering}
        />
      )}

      {yfOpen && (
        <YfinanceModal
          onClose={() => setYfOpen(false)}
          onDone={async (res) => {
            setYfOpen(false);
            const totalCreated = (res.created || []).length;
            const totalUpdated = (res.updated || []).length;
            const totalErrors = (res.errors || []).length;
            notify.success(`yfinance : ${totalCreated} créées, ${totalUpdated} maj, ${totalErrors} erreur(s).`);
            await load();
          }}
        />
      )}

      {detail && (
        <CompanyDetailModal
          data={detail}
          onClose={() => setDetail(null)}
          onEdit={() => { setEditor({ kind: "company", item: detail.company }); setDetail(null); }}
          onDelete={() => removeCompany(detail.company)}
          onAddFinancial={() => setEditor({ kind: "financial", companyId: detail.company.id })}
          onEditFinancial={(f) => setEditor({ kind: "financial", item: f, companyId: detail.company.id })}
          onRefreshAI={async () => {
            setDiscovering(true);
            try {
              const r = await api.post("/workspace/brvm/ai-discover", { tickers: [detail.company.ticker] });
              notify.success(`Rafraîchi : ${r.data.financials_imported} snapshots IA.`);
              const d = await api.get(`/workspace/brvm/companies/${detail.company.id}/detail`);
              setDetail(d.data);
              await load();
            } catch (e) { notify.error(fmtError(e)); }
            finally { setDiscovering(false); }
          }}
          refreshing={discovering}
        />
      )}

      {editor && (
        <CompanyOrFinancialEditor
          kind={editor.kind}
          item={editor.item}
          companyId={editor.companyId}
          countries={countries}
          exchanges={exchanges}
          sectorCats={sectorCats}
          onClose={() => setEditor(null)}
          onSaved={async () => {
            setEditor(null);
            await load();
            if (detail) {
              const d = await api.get(`/workspace/brvm/companies/${detail.company.id}/detail`);
              setDetail(d.data);
            }
          }}
        />
      )}
      </>)}
    </div>
  );
}

function StatCard({ label, value, suffix = "", color }) {
  return (
    <div className="noam-card p-3" data-testid={`brvm-stat-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <div className="text-[10px] uppercase tracking-wider text-zinc-500">{label}</div>
      <div className="text-xl font-bold mt-1" style={{ color: color || "#fafafa" }}>
        {value ?? "—"}<span className="text-[10px] text-zinc-500 font-normal ml-0.5">{suffix}</span>
      </div>
    </div>
  );
}

function DiscoverModal({ countries, exchanges, sectorCats, existingSectors, onClose, onDone, busy, setBusy }) {
  const [form, setForm] = useState({ count: 5, sector: "", sector_category: "", exchange: "", country: "" });
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post("/workspace/brvm/ai-discover", {
        count: form.count,
        sector: form.sector.trim(),
        sector_category: form.sector_category,
        exchange: form.exchange,
        country: form.country,
      });
      onDone(r.data);
    } catch (e) {
      notify.error(fmtError(e, "Découverte IA impossible."));
      setBusy(false);
    }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur flex items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="noam-card w-full max-w-lg my-8" onClick={(e) => e.stopPropagation()} data-testid="brvm-discover-modal">
        <div className="flex items-start justify-between p-5 border-b border-white/5">
          <div>
            <div className="noam-overline flex items-center gap-1.5"><Sparkles className="w-3 h-3 text-[#EF9F27]" /> IA hybride · NOAM + Tavily</div>
            <div className="noam-display text-base font-semibold mt-0.5">Découvrir des candidats sous-cotés</div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-white/5"><X className="w-4 h-4" /></button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-4">
          <p className="text-[11px] text-zinc-400 italic">
            L'IA recherche des actions sous-cotées sur la bourse choisie, importe les 5 dernières années de chiffres financiers (rapports annuels, sources web), et calcule tous les ratios value automatiquement.
          </p>
          <div>
            <label className="text-[11px] text-zinc-400 uppercase tracking-wider mb-1 block">Bourse cible</label>
            <select value={form.exchange} onChange={(e) => setForm({ ...form, exchange: e.target.value })} className="noam-input" data-testid="brvm-discover-exchange">
              <option value="">Aucune (par défaut : BRVM)</option>
              {exchanges.map((e) => <option key={e.code} value={e.code}>{e.code} — {e.name} ({e.currency})</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] text-zinc-400 uppercase tracking-wider mb-1 block">Catégorie sectorielle</label>
              <select value={form.sector_category} onChange={(e) => setForm({ ...form, sector_category: e.target.value })} className="noam-input" data-testid="brvm-discover-sector-category">
                <option value="">Toutes</option>
                {sectorCats.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[11px] text-zinc-400 uppercase tracking-wider mb-1 block">Nombre</label>
              <input type="number" min="1" max="10" value={form.count} onChange={(e) => setForm({ ...form, count: Math.max(1, Math.min(10, Number(e.target.value) || 5)) })} className="noam-input" data-testid="brvm-discover-count" />
            </div>
          </div>
          <div>
            <label className="text-[11px] text-zinc-400 uppercase tracking-wider mb-1 block">Secteur libre (mot-clé)</label>
            <input list="sectors-dl" value={form.sector} onChange={(e) => setForm({ ...form, sector: e.target.value })} placeholder="Banque, Cloud, Semi-conducteurs, Biotech…" className="noam-input" data-testid="brvm-discover-sector" />
            <datalist id="sectors-dl">
              {["Banque", "Cloud", "Semi-conducteurs", "Biotech", "Pétrole & Gaz", "Mines & Métaux", "Distribution", "Assurance", "Aéronautique", "Pharmacie", "Énergies renouvelables"].map((s) => <option key={s} value={s} />)}
              {existingSectors.map((s) => <option key={s} value={s} />)}
            </datalist>
          </div>
          <div>
            <label className="text-[11px] text-zinc-400 uppercase tracking-wider mb-1 block">Pays UEMOA (uniquement pour BRVM)</label>
            <select value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} className="noam-input" data-testid="brvm-discover-country" disabled={form.exchange && form.exchange !== "BRVM"}>
              <option value="">Tous les pays</option>
              {countries.map((c) => <option key={c.code} value={c.code}>{COUNTRY_FLAGS[c.code]} {c.name}</option>)}
            </select>
          </div>
          <div className="flex items-center justify-end gap-2 pt-3 border-t border-white/5">
            <button type="button" onClick={onClose} className="btn-ghost text-xs">Annuler</button>
            <button type="submit" disabled={busy} className="btn-primary text-xs disabled:opacity-50" data-testid="brvm-discover-submit">
              {busy ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> IA en cours (15-30s)…</> : <><Sparkles className="w-3.5 h-3.5" /> Lancer la découverte</>}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function CompanyDetailModal({ data, onClose, onEdit, onDelete, onAddFinancial, onEditFinancial, onRefreshAI, refreshing }) {
  const { company, financials, metrics } = data;
  const v = VERDICT_META[metrics.verdict] || VERDICT_META.fair;
  const chartData = (financials || []).map((f) => ({
    year: f.year,
    "Chiffre d'affaires": f.revenue,
    "Résultat ord. (RAO)": f.ordinary_result,
    "Résultat net": f.net_income,
    "Capitaux propres": f.equity,
  }));

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur flex items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="noam-card w-full max-w-5xl my-8" onClick={(e) => e.stopPropagation()} data-testid="brvm-detail-modal">
        <div className="flex items-start justify-between p-5 border-b border-white/5">
          <div>
            <div className="flex items-center gap-2">
              <div className="font-mono text-2xl font-bold text-[#378ADD]" data-testid="brvm-detail-ticker">{company.ticker}</div>
              <div className="px-2 py-1 rounded text-[10px] uppercase tracking-wider" style={{ background: v.bg, color: v.color }}>
                {v.label} · {metrics.composite_score?.toFixed(0) ?? "—"}/100
              </div>
            </div>
            <div className="noam-display text-base font-semibold mt-1">{company.name}</div>
            <div className="text-[11px] text-zinc-400">
              {COUNTRY_FLAGS[company.country]} {company.country} · {company.sector || "—"}
              {company.website && <> · <a href={company.website} target="_blank" rel="noopener noreferrer" className="text-[#378ADD] hover:underline">site <ArrowUpRight className="w-3 h-3 inline" /></a></>}
            </div>
            {company.notes && <p className="text-xs text-zinc-300 mt-2 max-w-2xl italic">"{company.notes}"</p>}
          </div>
          <div className="flex items-center gap-1">
            <button onClick={onRefreshAI} disabled={refreshing} className="btn-ghost text-xs" title="Rafraîchir les chiffres via IA" data-testid="brvm-detail-refresh-ai">
              {refreshing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
            </button>
            <button onClick={onEdit} className="p-1.5 rounded hover:bg-white/5" title="Éditer"><Edit2 className="w-4 h-4" /></button>
            <button onClick={onDelete} className="p-1.5 rounded hover:bg-white/5 text-[#E24B4A]" title="Supprimer"><Trash2 className="w-4 h-4" /></button>
            <button onClick={onClose} className="p-1.5 rounded hover:bg-white/5"><X className="w-4 h-4" /></button>
          </div>
        </div>

        <div className="p-5 space-y-5 max-h-[80vh] overflow-y-auto">
          {/* Ratios grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <RatioCard label="B/M" value={fmt.num(metrics.book_to_market)} hint="Book-to-Market" />
            <RatioCard label="P/B" value={fmt.num(metrics.price_to_book)} hint="Price-to-Book" />
            <RatioCard label="PER" value={fmt.num(metrics.per)} hint="Price/Earnings" />
            <RatioCard label="E/P" value={fmt.pct(metrics.earnings_yield)} hint="Earnings Yield" />
            <RatioCard label="CAGR CA" value={fmt.pct(metrics.cagr_revenue)} hint={`Sur ${metrics.years_span} ans`} />
            <RatioCard label="CAGR RAO" value={fmt.pct(metrics.cagr_rao)} hint="Activités ordinaires" />
            <RatioCard label="CAGR RN" value={fmt.pct(metrics.cagr_net_income)} hint="Résultat net" />
            <RatioCard label="Marge nette" value={fmt.pct(metrics.net_margin_avg)} hint="Moyenne 5Y" />
            <RatioCard label="Marge RAO" value={fmt.pct(metrics.rao_margin_avg)} hint="Moyenne 5Y" />
            <RatioCard label="ROE" value={fmt.pct(metrics.roe_avg)} hint="Moyenne 5Y" />
            <RatioCard label="EPS" value={fmt.num(metrics.eps)} hint="Bénéfice / Action" />
            <RatioCard label="Capi" value={fmt.bigM(metrics.market_cap)} hint="Capitalisation" />
          </div>

          {/* Composite score breakdown */}
          {metrics.score_components && (
            <div className="noam-card p-4" data-testid="brvm-detail-score-breakdown">
              <div className="noam-overline mb-2">Décomposition du score composite</div>
              <ResponsiveContainer width="100%" height={140}>
                <BarChart data={Object.entries(metrics.score_components).map(([k, v]) => ({
                  name: ({ book_to_market: "B/M", earnings_yield: "E/P", cagr_rao: "CAGR RAO", cagr_revenue: "CAGR CA", net_margin_avg: "Marge", roe_avg: "ROE" })[k] || k,
                  Contribution: Math.round(v * 100),
                }))}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                  <XAxis dataKey="name" tick={{ fontSize: 10, fill: "#a1a1aa" }} />
                  <YAxis tick={{ fontSize: 10, fill: "#a1a1aa" }} domain={[0, 100]} />
                  <Tooltip contentStyle={{ background: "#0a0a0a", border: "1px solid #27272a", fontSize: 11 }} />
                  <Bar dataKey="Contribution" fill="#1D9E75" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* 5-year evolution */}
          {chartData.length > 0 && (
            <div className="noam-card p-4" data-testid="brvm-detail-chart">
              <div className="flex items-center justify-between mb-2">
                <div className="noam-overline">Évolution {chartData[0].year} → {chartData[chartData.length - 1].year} (millions XOF)</div>
                <button onClick={onAddFinancial} className="btn-ghost text-xs" data-testid="brvm-detail-add-financial">
                  <Plus className="w-3 h-3" /> Année
                </button>
              </div>
              <ResponsiveContainer width="100%" height={240}>
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                  <XAxis dataKey="year" tick={{ fontSize: 10, fill: "#a1a1aa" }} />
                  <YAxis tick={{ fontSize: 10, fill: "#a1a1aa" }} />
                  <Tooltip contentStyle={{ background: "#0a0a0a", border: "1px solid #27272a", fontSize: 11 }} formatter={(v) => v?.toLocaleString("fr-FR") ?? "—"} />
                  <Legend wrapperStyle={{ fontSize: 10 }} />
                  <Line type="monotone" dataKey="Chiffre d'affaires" stroke="#378ADD" strokeWidth={2} dot={{ r: 3 }} />
                  <Line type="monotone" dataKey="Résultat ord. (RAO)" stroke="#EF9F27" strokeWidth={2} dot={{ r: 3 }} />
                  <Line type="monotone" dataKey="Résultat net" stroke="#1D9E75" strokeWidth={2} dot={{ r: 3 }} />
                  <Line type="monotone" dataKey="Capitaux propres" stroke="#C26EE7" strokeWidth={2} dot={{ r: 3 }} strokeDasharray="3 3" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Financials table */}
          <div className="noam-card overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b border-white/5">
              <div className="noam-overline">Snapshots financiers ({financials.length})</div>
              <button onClick={onAddFinancial} className="btn-ghost text-xs">
                <Plus className="w-3 h-3" /> Ajouter
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-white/[0.02]">
                  <tr className="text-left text-[10px] uppercase tracking-wider text-zinc-500">
                    <th className="px-3 py-2">Année</th>
                    <th className="px-3 py-2 text-right">CA</th>
                    <th className="px-3 py-2 text-right">RAO</th>
                    <th className="px-3 py-2 text-right">RN</th>
                    <th className="px-3 py-2 text-right">Capitaux propres</th>
                    <th className="px-3 py-2 text-right">Total actifs</th>
                    <th className="px-3 py-2">Source</th>
                    <th className="px-3 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {financials.length === 0 && (
                    <tr><td colSpan={8} className="text-center py-6 text-zinc-500 italic">Aucun snapshot. Cliquez Ajouter ou utilisez l'IA.</td></tr>
                  )}
                  {financials.map((f) => (
                    <tr key={f.year} className="border-t border-white/5 hover:bg-white/[0.02]" data-testid={`brvm-detail-fin-${f.year}`}>
                      <td className="px-3 py-2 font-mono font-bold">{f.year}</td>
                      <td className="px-3 py-2 text-right font-mono">{f.revenue?.toLocaleString("fr-FR") ?? "—"}</td>
                      <td className="px-3 py-2 text-right font-mono">{f.ordinary_result?.toLocaleString("fr-FR") ?? "—"}</td>
                      <td className="px-3 py-2 text-right font-mono">{f.net_income?.toLocaleString("fr-FR") ?? "—"}</td>
                      <td className="px-3 py-2 text-right font-mono">{f.equity?.toLocaleString("fr-FR") ?? "—"}</td>
                      <td className="px-3 py-2 text-right font-mono">{f.total_assets?.toLocaleString("fr-FR") ?? "—"}</td>
                      <td className="px-3 py-2 text-[10px] text-zinc-500 italic">{f.source}</td>
                      <td className="px-3 py-2 text-right">
                        <button onClick={() => onEditFinancial(f)} className="text-zinc-500 hover:text-white p-1"><Edit2 className="w-3 h-3" /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function RatioCard({ label, value, hint }) {
  return (
    <div className="border border-white/5 rounded p-2.5">
      <div className="text-[10px] uppercase tracking-wider text-zinc-500">{label}</div>
      <div className="text-base font-bold font-mono mt-0.5">{value}</div>
      <div className="text-[9px] text-zinc-600 mt-0.5">{hint}</div>
    </div>
  );
}

function CompanyOrFinancialEditor({ kind, item, companyId, countries, exchanges = [], sectorCats = [], onClose, onSaved }) {
  const isCompany = kind === "company";
  const [form, setForm] = useState(() => {
    if (isCompany) {
      return item || { ticker: "", name: "", sector: "", sector_category: "", exchange: "BRVM", country: "CI", shares_outstanding: 0, price: 0, currency: "XOF", website: "", notes: "" };
    }
    return item || { year: new Date().getFullYear() - 1, revenue: null, ordinary_result: null, net_income: null, equity: null, total_assets: null, dividend_per_share: null, source: "manual" };
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      if (isCompany) {
        const payload = {
          ticker: form.ticker, name: form.name, sector: form.sector,
          sector_category: form.sector_category,
          exchange: form.exchange || "BRVM",
          country: form.country,
          shares_outstanding: Number(form.shares_outstanding) || 0,
          price: Number(form.price) || 0,
          currency: form.currency || "XOF",
          website: form.website, notes: form.notes,
        };
        if (item?.id) await api.patch(`/workspace/brvm/companies/${item.id}`, payload);
        else await api.post("/workspace/brvm/companies", payload);
        notify.success(item?.id ? "Compagnie mise à jour." : "Compagnie créée.");
      } else {
        const payload = {
          year: Number(form.year),
          revenue: form.revenue !== null && form.revenue !== "" ? Number(form.revenue) : null,
          ordinary_result: form.ordinary_result !== null && form.ordinary_result !== "" ? Number(form.ordinary_result) : null,
          net_income: form.net_income !== null && form.net_income !== "" ? Number(form.net_income) : null,
          equity: form.equity !== null && form.equity !== "" ? Number(form.equity) : null,
          total_assets: form.total_assets !== null && form.total_assets !== "" ? Number(form.total_assets) : null,
          dividend_per_share: form.dividend_per_share !== null && form.dividend_per_share !== "" ? Number(form.dividend_per_share) : null,
          source: form.source || "manual",
        };
        await api.post(`/workspace/brvm/companies/${companyId}/financials`, payload);
        notify.success("Snapshot enregistré.");
      }
      onSaved();
    } catch (e) { setErr(fmtError(e, "Erreur.")); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur flex items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="noam-card w-full max-w-xl my-8" onClick={(e) => e.stopPropagation()} data-testid={isCompany ? "brvm-company-editor" : "brvm-financial-editor"}>
        <div className="flex items-start justify-between p-5 border-b border-white/5">
          <div className="noam-display text-base font-semibold">
            {isCompany ? (item?.id ? "Éditer la compagnie" : "Nouvelle compagnie") : `Snapshot ${form.year}`}
          </div>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-white/5"><X className="w-4 h-4" /></button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-3 max-h-[75vh] overflow-y-auto">
          {isCompany ? (
            <>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="text-[11px] text-zinc-400 uppercase tracking-wider mb-1 block">Ticker *</label>
                  <input required value={form.ticker} onChange={(e) => setForm({ ...form, ticker: e.target.value.toUpperCase() })} placeholder="SNTS, AAPL…" className="noam-input font-mono" data-testid="brvm-form-ticker" />
                </div>
                <div>
                  <label className="text-[11px] text-zinc-400 uppercase tracking-wider mb-1 block">Bourse</label>
                  <select value={form.exchange || "BRVM"} onChange={(e) => {
                    const ex = e.target.value;
                    const meta = exchanges.find((x) => x.code === ex);
                    setForm({ ...form, exchange: ex, currency: meta?.currency || form.currency });
                  }} className="noam-input" data-testid="brvm-form-exchange">
                    {exchanges.map((e) => <option key={e.code} value={e.code}>{e.code}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-[11px] text-zinc-400 uppercase tracking-wider mb-1 block">Pays</label>
                  <select value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} className="noam-input" data-testid="brvm-form-country">
                    {countries.map((c) => <option key={c.code} value={c.code}>{COUNTRY_FLAGS[c.code] || ""} {c.name}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="text-[11px] text-zinc-400 uppercase tracking-wider mb-1 block">Nom *</label>
                <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="noam-input" data-testid="brvm-form-name" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[11px] text-zinc-400 uppercase tracking-wider mb-1 block">Catégorie sectorielle</label>
                  <select value={form.sector_category || ""} onChange={(e) => setForm({ ...form, sector_category: e.target.value })} className="noam-input" data-testid="brvm-form-sector-category">
                    <option value="">—</option>
                    {sectorCats.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-[11px] text-zinc-400 uppercase tracking-wider mb-1 block">Secteur libre</label>
                  <input value={form.sector} onChange={(e) => setForm({ ...form, sector: e.target.value })} placeholder="Banque, Cloud, Biotech…" className="noam-input" />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="text-[11px] text-zinc-400 uppercase tracking-wider mb-1 block">Devise</label>
                  <input value={form.currency || "XOF"} onChange={(e) => setForm({ ...form, currency: e.target.value.toUpperCase() })} className="noam-input font-mono" />
                </div>
                <div>
                  <label className="text-[11px] text-zinc-400 uppercase tracking-wider mb-1 block">Nb actions</label>
                  <input type="number" value={form.shares_outstanding} onChange={(e) => setForm({ ...form, shares_outstanding: e.target.value })} className="noam-input font-mono" data-testid="brvm-form-shares" />
                </div>
                <div>
                  <label className="text-[11px] text-zinc-400 uppercase tracking-wider mb-1 block">Cours</label>
                  <input type="number" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="noam-input font-mono" data-testid="brvm-form-price" />
                </div>
              </div>
              <div>
                <label className="text-[11px] text-zinc-400 uppercase tracking-wider mb-1 block">Site web</label>
                <input value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} placeholder="https://…" className="noam-input" />
              </div>
              <div>
                <label className="text-[11px] text-zinc-400 uppercase tracking-wider mb-1 block">Notes / Thèse value</label>
                <textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="noam-input" />
              </div>
            </>
          ) : (
            <>
              <div>
                <label className="text-[11px] text-zinc-400 uppercase tracking-wider mb-1 block">Année *</label>
                <input type="number" required min="1990" max="2100" value={form.year} onChange={(e) => setForm({ ...form, year: e.target.value })} className="noam-input w-32 font-mono" data-testid="brvm-form-year" />
              </div>
              <p className="text-[10px] text-zinc-500 italic">Montants en millions de FCFA (XOF). Laissez vide si la donnée est manquante.</p>
              {[
                ["revenue", "Chiffre d'affaires (CA)"],
                ["ordinary_result", "Résultat des Activités Ordinaires (RAO)"],
                ["net_income", "Résultat Net (RN)"],
                ["equity", "Capitaux propres"],
                ["total_assets", "Total actifs"],
                ["dividend_per_share", "Dividende par action (XOF)"],
              ].map(([key, label]) => (
                <div key={key}>
                  <label className="text-[11px] text-zinc-400 uppercase tracking-wider mb-1 block">{label}</label>
                  <input type="number" step="0.01" value={form[key] ?? ""} onChange={(e) => setForm({ ...form, [key]: e.target.value === "" ? null : e.target.value })} className="noam-input font-mono" data-testid={`brvm-form-${key}`} />
                </div>
              ))}
            </>
          )}

          {err && <div className="text-xs text-[#FCA5A5] p-2 rounded border border-[#E24B4A]/30 bg-[#E24B4A]/10">{err}</div>}

          <div className="flex items-center justify-end gap-2 pt-3 border-t border-white/5">
            <button type="button" onClick={onClose} className="btn-ghost text-xs">Annuler</button>
            <button type="submit" disabled={busy} className="btn-primary text-xs disabled:opacity-50" data-testid="brvm-form-submit">
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
              {item?.id ? "Enregistrer" : "Créer"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Sortable table header
function Th({ children, onClick, active, dir, right = false, title }) {
  return (
    <th
      onClick={onClick}
      title={title}
      className={`px-3 py-2.5 cursor-pointer select-none hover:text-white transition-colors ${right ? "text-right" : ""}`}
    >
      <span className="inline-flex items-center gap-1">
        {children}
        {active && (<span className="text-[8px] text-[#378ADD]">{dir === "asc" ? "▲" : "▼"}</span>)}
      </span>
    </th>
  );
}

// P0 — Modal d'import yfinance (bulk + ticker unique)
function YfinanceModal({ onClose, onDone }) {
  const [tickersText, setTickersText] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    const tickers = tickersText
      .split(/[,\s\n]+/)
      .map((t) => t.trim().toUpperCase())
      .filter(Boolean)
      .slice(0, 20);
    if (!tickers.length) {
      notify.error("Saisissez au moins un ticker.");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post("/workspace/brvm/yfinance-create", { tickers });
      onDone(r.data);
    } catch (e) {
      notify.error(fmtError(e, "Import yfinance impossible."));
      setBusy(false);
    }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur flex items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="noam-card w-full max-w-lg my-8" onClick={(e) => e.stopPropagation()} data-testid="brvm-yfinance-modal">
        <div className="flex items-start justify-between p-5 border-b border-white/5">
          <div>
            <div className="noam-overline flex items-center gap-1.5"><Globe className="w-3 h-3 text-[#1D9E75]" /> yfinance · données réelles</div>
            <div className="noam-display text-base font-semibold mt-0.5">Importer depuis Yahoo Finance</div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-white/5"><X className="w-4 h-4" /></button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-4">
          <p className="text-[11px] text-zinc-400 italic">
            Pull prix réel + 5 ans de financials via yfinance pour NYSE / NASDAQ / Euronext (suffixe .PA) / LSE (.L) / TSX (.TO) / B3 (.SA) / etc.<br />
            <strong className="text-[#E24B4A]">Non couvert :</strong> BRVM, JSE, EGX, BVMT — utilisez "Découvrir par IA".
          </p>
          <div>
            <label className="text-[11px] text-zinc-400 uppercase tracking-wider mb-1 block">Tickers (max 20, séparés par espace, virgule ou ligne)</label>
            <textarea
              required
              rows={5}
              value={tickersText}
              onChange={(e) => setTickersText(e.target.value)}
              placeholder="AAPL MSFT NVDA&#10;MC.PA TTE.PA&#10;HSBA.L"
              className="noam-input font-mono text-sm"
              data-testid="brvm-yf-tickers"
              autoFocus
            />
          </div>
          <div className="flex items-center justify-end gap-2 pt-3 border-t border-white/5">
            <button type="button" onClick={onClose} className="btn-ghost text-xs">Annuler</button>
            <button type="submit" disabled={busy} className="btn-primary text-xs disabled:opacity-50" data-testid="brvm-yf-submit">
              {busy ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Import en cours…</> : <><Globe className="w-3.5 h-3.5" /> Importer</>}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

